// RoastPulse - Coffee Roaster Feedback Platform
import { lazy, Suspense } from "react";
import * as Sentry from "@sentry/react";
import { Analytics } from "@vercel/analytics/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";

// Public pages
import Home from "./pages/public/Home";
const Login = lazy(() => import("./pages/public/Login"));
const Signup = lazy(() => import("./pages/public/Signup"));
const FeedbackForm = lazy(() => import("./pages/public/FeedbackForm"));
const RoasterPublicPage = lazy(() => import("./pages/public/RoasterPublicPage"));
const PrivacyPolicy = lazy(() => import("./pages/public/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/public/TermsOfUse"));

// Internal pages (protected)
const Overview = lazy(() => import("./pages/internal/Overview"));
const Products = lazy(() => import("./pages/internal/Products"));
const ProductDetail = lazy(() => import("./pages/internal/ProductDetail"));
const Lots = lazy(() => import("./pages/internal/Lots"));
const LotDetail = lazy(() => import("./pages/internal/LotDetail"));
const Integrations = lazy(() => import("./pages/internal/Integrations"));
const Profiles = lazy(() => import("./pages/internal/Profiles"));
const Onboarding = lazy(() => import("./pages/internal/Onboarding"));
const Settings = lazy(() => import("./pages/internal/Settings"));

// Shared pages
const NotFound = lazy(() => import("./pages/shared/NotFound"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center p-4">
    <div className="w-full max-w-md space-y-4">
      <Skeleton className="h-8 w-3/4 mx-auto" />
      <Skeleton className="h-64 w-full rounded-xl" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  </div>
);

// Protected route wrapper for authenticated roasters
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Smart redirect component that routes based on auth state
const RootRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  // Authenticated users go to dashboard, guests go to home
  return <Navigate to={user ? "/dashboard" : "/home"} replace />;
};

// Error fallback component for Sentry ErrorBoundary
const SentryErrorFallback = ({ error }: { error: Error }) => (
  <div className="min-h-screen flex items-center justify-center p-4">
    <div className="w-full max-w-md text-center space-y-4">
      <h1 className="text-2xl font-semibold text-destructive">Something went wrong</h1>
      <p className="text-muted-foreground">
        We've been notified and are working to fix the issue.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
      >
        Reload page
      </button>
      {import.meta.env.DEV && (
        <pre className="mt-4 p-4 bg-muted rounded-md text-left text-xs overflow-auto">
          {error.message}
        </pre>
      )}
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      throwOnError: true,
    },
    mutations: {
      onError: (error) => {
        Sentry.captureException(error);
      },
    },
  },
});

const App = () => (
  <Sentry.ErrorBoundary fallback={SentryErrorFallback}>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <Analytics />
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<RootRedirect />} />
                  <Route path="/home" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfUse />} />
                  
                  {/* Public feedback form - no auth required */}
                  <Route path="/f/:qrCodeId" element={<FeedbackForm />} />
                  
                  {/* Public roaster page - no auth required */}
                  <Route path="/r/:slug" element={<RoasterPublicPage />} />
                  
                  {/* Protected roaster routes */}
                  <Route path="/onboard" element={
                    <ProtectedRoute>
                      <Onboarding />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <Overview />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/products" element={
                    <ProtectedRoute>
                      <Products />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/products/:id" element={
                    <ProtectedRoute>
                      <ProductDetail />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/products/:productId/lots" element={
                    <ProtectedRoute>
                      <Lots />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/lots/:lotId" element={
                    <ProtectedRoute>
                      <LotDetail />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/settings" element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/integrations" element={
                    <ProtectedRoute>
                      <Integrations />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/profiles" element={
                    <ProtectedRoute>
                      <Profiles />
                    </ProtectedRoute>
                  } />
                
                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </LanguageProvider>
  </QueryClientProvider>
</Sentry.ErrorBoundary>
);

export default App;
